import { useEffect, useRef, useState } from 'react';
import { DetailShell } from '../../detail/DetailShell';
import { SectionTitle } from '../../detail/SectionTitle';
import { getActiveContacts } from '@/services/mockData';

const MONO_LABEL = {
  fontFamily: 'var(--mono)', fontSize: 8, textTransform: 'uppercase',
  letterSpacing: '0.06em', color: 'var(--ink-4)', marginBottom: 2,
};

const INPUT = {
  fontFamily: 'var(--sans)', fontSize: 11, color: 'var(--ink)',
  background: 'transparent', border: '1px solid var(--hairline)',
  padding: '4px 7px', width: '100%', outline: 'none', boxSizing: 'border-box', display: 'block',
};

function blankContact() {
  return {
    id: `con_${Date.now()}`,
    firm: '',
    firm_url: '',
    viable: 'TBC',
    connect: '',
    connect_url: '',
    position: '',
    connect_level: 'L1',
    route_to_reach: '',
    route_url: '',
    route_level: 'L1',
    history: [],
    commentary: '',
  };
}

// ── Chips ─────────────────────────────────────────────────────────────────────

function ViableChip({ viable }) {
  if (viable === 'Yes')      return <span className="chip" style={{ background: 'var(--pos-soft)', borderColor: 'var(--pos)', color: 'var(--accent-ink)', fontSize: 9, padding: '1px 5px' }}>YES</span>;
  if (viable === 'Possible') return <span className="chip" style={{ background: 'var(--warn-soft)', borderColor: 'var(--warn)', fontSize: 9, padding: '1px 5px' }}>POSSIBLE</span>;
  return <span className="chip muted" style={{ fontSize: 9, padding: '1px 5px' }}>TBC</span>;
}

function LevelChip({ level }) {
  return (
    <span className="chip muted" style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '1px 5px', letterSpacing: '0.04em' }}>
      {level}
    </span>
  );
}

function ExtLink({ href, children, style }) {
  if (!href) return <span style={style}>{children}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ color: 'inherit', textDecoration: 'none', ...style }}
    >
      {children}<span style={{ marginLeft: 2, fontSize: 7, opacity: 0.45 }}>↗</span>
    </a>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(dateStr) {
  if (!dateStr || dateStr === 'tbc') return 'TBC';
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function dueStatus(dateStr) {
  if (!dateStr || dateStr === 'tbc') return 'ok';
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  const days = (due - now) / 86400000;
  if (days < 0) return 'overdue';
  if (days <= 7) return 'due-soon';
  return 'ok';
}

function DueTag({ status }) {
  if (status === 'overdue') {
    return (
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase',
        background: 'var(--warn-soft)', border: '1px solid var(--warn)',
        padding: '0px 4px', color: 'var(--ink)', flexShrink: 0,
      }}>! OVERDUE</span>
    );
  }
  if (status === 'due-soon') {
    return (
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase',
        background: 'var(--accent-soft)', border: '1px solid var(--accent)',
        padding: '0px 4px', color: 'var(--ink)', flexShrink: 0,
      }}>◷ DUE SOON</span>
    );
  }
  return null;
}

// ── Segmented control ─────────────────────────────────────────────────────────

function SegmentedSelect({ value, options, onChange }) {
  return (
    <div style={{ display: 'inline-flex', gap: 3 }}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          style={{
            fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.04em',
            textTransform: 'uppercase', padding: '2px 8px', cursor: 'pointer',
            border: `1px solid ${value === opt ? 'var(--ink)' : 'var(--hairline)'}`,
            background: value === opt ? 'var(--ink)' : 'transparent',
            color: value === opt ? 'var(--paper)' : 'var(--ink-3)',
          }}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function UrlField({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 7 }}>
      <label style={{ ...MONO_LABEL, display: 'block' }}>{label}</label>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="url"
          value={value}
          onChange={onChange}
          placeholder="https://www.linkedin.com/…"
          style={INPUT}
        />
        {value && (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)',
              border: '1px solid var(--hairline)', padding: '0 8px',
              textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center',
            }}
          >
            ↗
          </a>
        )}
      </div>
    </div>
  );
}

const MOVE_BTN = {
  fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--ink-3)', background: 'transparent', border: '1px solid var(--ink-3)',
  padding: '3px 9px', cursor: 'pointer',
};

// ── Drill-in ──────────────────────────────────────────────────────────────────

function ContactDrillIn({ contact, isNew = false, onBack, onChange, onDelete, onMoveToOpp, onMoveToNurture }) {
  const set = (field) => (e) => onChange({ [field]: e.target.value });

  const addStep = () => onChange({ history: [{ date: today(), note: '' }, ...contact.history] });

  const updateStep = (i, patch) =>
    onChange({ history: contact.history.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) });

  const deleteStep = (i) =>
    onChange({ history: contact.history.filter((_, idx) => idx !== i) });

  return (
    <div style={{ gridColumn: '1 / -1' }}>

      {/* Nav row */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 14 }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
            color: 'var(--ink-3)', background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 0, display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          CONTACTS →
        </button>
      </div>

      {/* Identity */}
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ marginBottom: 10 }}>
          <label style={{ ...MONO_LABEL, display: 'block' }}>Firm</label>
          <input
            style={{ ...INPUT, fontSize: 18, fontFamily: 'var(--serif)', padding: '4px 7px' }}
            value={contact.firm}
            onChange={set('firm')}
            placeholder="Company name…"
          />
        </div>
        <UrlField label="Company LinkedIn" value={contact.firm_url} onChange={set('firm_url')} />
        <div>
          <label style={{ ...MONO_LABEL, display: 'block', marginBottom: 4 }}>Viable</label>
          <SegmentedSelect
            value={contact.viable}
            options={['Yes', 'Possible', 'TBC']}
            onChange={(v) => onChange({ viable: v })}
          />
        </div>
      </div>

      {/* LinkedIn Connect + Route */}
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div>
            <SectionTitle label="LinkedIn Connect" />
            <div style={{ marginBottom: 7 }}>
              <label style={{ ...MONO_LABEL, display: 'block' }}>Name</label>
              <input style={INPUT} value={contact.connect} onChange={set('connect')} placeholder="Full name…" />
            </div>
            <UrlField label="LinkedIn URL" value={contact.connect_url} onChange={set('connect_url')} />
            <div style={{ marginBottom: 7 }}>
              <label style={{ ...MONO_LABEL, display: 'block' }}>Position</label>
              <input style={INPUT} value={contact.position} onChange={set('position')} placeholder="Job title…" />
            </div>
            <div>
              <label style={{ ...MONO_LABEL, display: 'block', marginBottom: 4 }}>Connect Level</label>
              <SegmentedSelect
                value={contact.connect_level}
                options={['L1', 'L2', 'L3']}
                onChange={(v) => onChange({ connect_level: v })}
              />
            </div>
          </div>
          <div>
            <SectionTitle label="Route to Reach" />
            <div style={{ marginBottom: 7 }}>
              <label style={{ ...MONO_LABEL, display: 'block' }}>Via</label>
              <input style={INPUT} value={contact.route_to_reach} onChange={set('route_to_reach')} placeholder="Full name…" />
            </div>
            <UrlField label="LinkedIn URL" value={contact.route_url} onChange={set('route_url')} />
            <div>
              <label style={{ ...MONO_LABEL, display: 'block', marginBottom: 4 }}>Route Level</label>
              <SegmentedSelect
                value={contact.route_level}
                options={['L1', 'L2', 'L3']}
                onChange={(v) => onChange({ route_level: v })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <SectionTitle label="Next Steps" meta={`${contact.history.length} ENTRIES`} />
          <button
            type="button"
            onClick={addStep}
            style={{
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--accent)', background: 'transparent', border: '1px solid var(--accent)',
              padding: '3px 9px', cursor: 'pointer',
            }}
          >
            + ADD STEP
          </button>
        </div>
        {contact.history.length === 0 && (
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
            NO STEPS YET — ADD ONE ABOVE
          </div>
        )}
        <div style={{ display: 'grid', gap: 6 }}>
          {contact.history.map((h, i) => (
            <div
              key={i}
              style={{
                display: 'grid', gridTemplateColumns: '140px 1fr 22px', gap: 6, alignItems: 'center',
                paddingBottom: i < contact.history.length - 1 ? 6 : 0,
                borderBottom: i < contact.history.length - 1 ? '1px dashed var(--hairline)' : 'none',
              }}
            >
              <input
                type="date"
                value={h.date === 'tbc' ? '' : h.date}
                onChange={(e) => updateStep(i, { date: e.target.value || 'tbc' })}
                style={{ ...INPUT, fontFamily: 'var(--mono)', fontSize: 10 }}
              />
              <input
                value={h.note}
                onChange={(e) => updateStep(i, { note: e.target.value })}
                style={{ ...INPUT, ...(i === 0 ? { fontWeight: 500 } : { color: 'var(--ink-3)' }) }}
                placeholder="Next step note…"
              />
              <button
                type="button"
                onClick={() => deleteStep(i)}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--ink-4)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Commentary */}
      <div className="card" style={{ marginBottom: 10 }}>
        <SectionTitle label="Commentary" />
        <textarea
          value={contact.commentary}
          onChange={set('commentary')}
          placeholder="Add commentary…"
          style={{ ...INPUT, minHeight: 80, resize: 'vertical', lineHeight: 1.5 }}
        />
      </div>

      {/* Transition Contact */}
      <div className="card">
        <SectionTitle label="Transition Contact" meta="MOVE CONTACT" />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {!isNew && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <button
                type="button"
                onClick={onDelete}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: 'var(--danger)', background: 'transparent', border: '1px solid var(--danger)',
                  padding: '3px 9px', cursor: 'pointer',
                }}
              >
                DELETE
              </button>
              {onMoveToOpp     && <button type="button" onClick={() => onMoveToOpp(contact)}     style={MOVE_BTN}>→ OPPORTUNITY</button>}
              {onMoveToNurture && <button type="button" onClick={() => onMoveToNurture(contact)} style={MOVE_BTN}>→ NURTURE CONTACT</button>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
                background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)',
                padding: '6px 12px', cursor: 'pointer',
              }}
            >
              {isNew ? 'Add' : 'Save'}
            </button>
            <button
              type="button"
              onClick={isNew ? onDelete : onBack}
              style={{
                fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
                background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--hairline)',
                padding: '6px 12px', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}

// ── Contact card ──────────────────────────────────────────────────────────────

function ContactCard({ contact, onClick }) {
  const current = contact.history?.[0];
  const wasDragged = useRef(false);
  const status = current ? dueStatus(current.date) : 'ok';

  const cardShadow = status === 'overdue'  ? 'inset 3px 0 0 0 var(--warn)'
                   : status === 'due-soon' ? 'inset 3px 0 0 0 var(--accent)'
                   : 'none';
  const dateColor  = status === 'overdue'  ? 'var(--warn)'
                   : status === 'due-soon' ? 'var(--accent)'
                   : 'var(--ink-4)';
  const nextBorder = status === 'overdue'  ? '1px dashed var(--warn)'
                   : status === 'due-soon' ? '1px dashed var(--accent)'
                   : '1px dashed var(--hairline)';

  return (
    <div
      onClick={() => { if (!wasDragged.current) onClick(); }}
      style={{ border: '1px solid var(--hairline)', padding: '7px 9px', background: 'var(--paper)', cursor: 'pointer', boxShadow: cardShadow, display: 'flex', flexDirection: 'column' }}
    >
      {/* Viable + connect level */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <ViableChip viable={contact.viable} />
        <LevelChip level={contact.connect_level} />
      </div>

      {/* Firm */}
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.2, marginBottom: 6 }}>
        <ExtLink href={contact.firm_url}>
          {contact.firm || <span style={{ color: 'var(--ink-4)', fontWeight: 400, fontStyle: 'italic' }}>Untitled</span>}
        </ExtLink>
      </div>

      {/* Connect */}
      <div style={{ marginBottom: 5 }}>
        <div style={MONO_LABEL}>Connect</div>
        <div style={{ fontSize: 10, color: 'var(--ink-2)', lineHeight: 1.3 }}>
          <ExtLink href={contact.connect_url}>{contact.connect || '—'}</ExtLink>
        </div>
        <div style={{ fontSize: 9, color: 'var(--ink-4)', lineHeight: 1.3 }}>{contact.position}</div>
      </div>

      {/* Route */}
      {contact.route_to_reach && (
        <div style={{ marginBottom: 5 }}>
          <div style={{ ...MONO_LABEL, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Route</span>
            <LevelChip level={contact.route_level} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--ink-2)', lineHeight: 1.3 }}>
            <ExtLink href={contact.route_url}>{contact.route_to_reach}</ExtLink>
          </div>
        </div>
      )}

      {/* Current next step */}
      {current && (
        <div style={{ borderTop: nextBorder, paddingTop: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={MONO_LABEL}>Next</div>
            <DueTag status={status} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '26px 1fr', gap: 4 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 8, color: dateColor, paddingTop: 1 }}>
              {fmtDate(current.date)}
            </span>
            <span style={{ fontSize: 9, color: 'var(--ink-2)', lineHeight: 1.3 }}>{current.note}</span>
          </div>
        </div>
      )}

      <div style={{ marginTop: 'auto', paddingTop: 6, textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--ink-4)', letterSpacing: '0.04em' }}>
        VIEW ›
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function ContactsDetail({ onClose, initialContacts = [], onMoveToOpp, onMoveToNurture }) {
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isNewContact, setIsNewContact] = useState(false);
  const initialRef = useRef(initialContacts);

  useEffect(() => {
    let cancelled = false;
    getActiveContacts().then((d) => { if (!cancelled) setContacts([...d, ...initialRef.current]); });
    return () => { cancelled = true; };
  }, []);

  const updateContact = (id, patch) =>
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const deleteContact = (id) => {
    setContacts((prev) => prev.filter((c) => c.id !== id));
    setSelectedId(null);
    setIsNewContact(false);
  };

  const addContact = () => {
    const c = blankContact();
    setContacts((prev) => [c, ...prev]);
    setSelectedId(c.id);
    setIsNewContact(true);
  };

  const selectedContact = selectedId ? contacts.find((c) => c.id === selectedId) : null;

  const total    = contacts.length;
  const yesCount = contacts.filter((c) => c.viable === 'Yes').length;
  const posCount = contacts.filter((c) => c.viable === 'Possible').length;
  const tbcCount = contacts.filter((c) => c.viable === 'TBC').length;

  const OVERVIEW = [
    { label: 'Total',    value: total,    bg: 'var(--paper-2)',     border: 'var(--hairline)' },
    { label: 'Viable',   value: yesCount, bg: 'var(--pos-soft)',    border: 'var(--pos)'      },
    { label: 'Possible', value: posCount, bg: 'var(--warn-soft)',   border: 'var(--warn)'     },
    { label: 'TBC',      value: tbcCount, bg: 'var(--paper-2)',     border: 'var(--ink-3)'    },
  ];

  return (
    <DetailShell eyebrow="02 · Pipeline Builder" title="Active Contacts" onClose={onClose}>

      {selectedContact ? (
        <ContactDrillIn
          contact={selectedContact}
          isNew={isNewContact}
          onBack={() => { setSelectedId(null); setIsNewContact(false); }}
          onChange={(patch) => updateContact(selectedContact.id, patch)}
          onDelete={() => deleteContact(selectedContact.id)}
          onMoveToOpp={onMoveToOpp ? (c) => { deleteContact(c.id); onMoveToOpp(c); } : null}
          onMoveToNurture={onMoveToNurture ? (c) => { deleteContact(c.id); onMoveToNurture(c); } : null}
        />
      ) : (
        <>
          {/* Overview */}
          <div className="card">
            <SectionTitle label="Contact overview" meta={`${total} ACTIVE · 30-DAY TOUCH CYCLE`} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {OVERVIEW.map((o) => (
                <div key={o.label} style={{ border: `1px solid ${o.border}`, padding: '8px 10px', background: o.bg }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>{o.label}</div>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 26, lineHeight: 1 }}>{o.value}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', marginTop: 2 }}>CONTACTS</div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact list */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <SectionTitle label="Contact list" meta={`${total} TOTAL`} />
              <button
                type="button"
                onClick={addContact}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: 'var(--accent)', background: 'transparent', border: '1px solid var(--accent)',
                  padding: '3px 10px', cursor: 'pointer',
                }}
              >
                + NEW CONTACT
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {contacts.map((c) => (
                <ContactCard key={c.id} contact={c} onClick={() => setSelectedId(c.id)} />
              ))}
              {contacts.length === 0 && (
                <div style={{ gridColumn: '1 / -1', border: '1px dashed var(--hairline)', padding: '24px', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
                  NO CONTACTS — ADD ONE ABOVE
                </div>
              )}
            </div>
          </div>
        </>
      )}

    </DetailShell>
  );
}

export default ContactsDetail;
